import React, { useState, useEffect, useRef } from 'react';
import { Download, AlertCircle } from 'lucide-react';
import { collection, query, where, getDocs, limit, documentId, doc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from './firebase';
import LoginModal from './LoginModal';
import AddWordModal from './AddWordModal';
import ProfileModal from './ProfileModal';
import SearchModal from './SearchModal';
import Navbar from './Navbar';
import WordCard from './WordCard';

import { translations } from './lang';
import ThemePalette from './ThemePalette';

const normalizeSearch = (text) => {
  if (!text) return '';
  return text
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')
    .replace(/ı/g, 'i')
    .toLowerCase()
    .replace(/\u0307/g, ''); // Remove combining dot often produced by JS toLowerCase on İ
};

const NeobrutalDictionary = ({ user, isAdmin, lang, setLang, theme, setTheme }) => {
  const t = translations[lang] || translations['en'];
  const [data, setData] = useState(null);
  const [recentWords, setRecentWords] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // const [theme, setTheme] = useState('yellow'); // Hoisted to App.js
  // const [user, setUser] = useState(null); // Hoisted to App.js
  const [showLogin, setShowLogin] = useState(false);
  const [showAddWord, setShowAddWord] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const cardRef = useRef(null);

  const headerRef = useRef(null);
  const [headerFontSize, setHeaderFontSize] = useState(null);

  useEffect(() => {
    const adjustHeaderFont = () => {
      const element = headerRef.current;
      if (!element) return;

      const isMobile = window.innerWidth < 768;
      let currentSize = isMobile ? 48 : 72;
      const minSize = 24;

      element.style.fontSize = `${currentSize}px`;
      element.style.whiteSpace = 'nowrap';

      while (element.scrollWidth > element.clientWidth && currentSize > minSize) {
        currentSize--;
        element.style.fontSize = `${currentSize}px`;
      }

      if (element.scrollWidth > element.clientWidth) {
        element.style.whiteSpace = 'normal';
      } else {
        element.style.whiteSpace = 'nowrap';
      }

      setHeaderFontSize(currentSize);
    };

    adjustHeaderFont();
    window.addEventListener('resize', adjustHeaderFont);
    return () => window.removeEventListener('resize', adjustHeaderFont);
  }, []);

  // const isAdmin calculated in parent

  const handleEditWord = (wordData) => {
    setEditingWord(wordData);
    setShowProfile(false);
    setShowAddWord(true); // Re-use the Add Modal for editing
  };

  // Load Words (Random by default)
  const fetchRandomWords = async (isAppend = false) => {
    try {
      if (isAppend) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // 1. Fetch Pinned Words
      let pinnedWords = [];
      if (!isAppend) {
        const pinnedQ = query(collection(db, "words"), where("pinned", "==", true));
        const pinnedSnapshot = await getDocs(pinnedQ);
        pinnedWords = pinnedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      // 2. Fetch Random Words (simulate random by fetching a batch and shuffling)
      // Since we want "Random" on refresh, we can't just rely on 'recent'.
      // We will fetch a larger batch of recent words and shuffle them, 
      // OR use the random ID startAt method for true randomness.
      // Let's use the Random ID method for better "Random" feel.

      const batchSize = 5;
      const randomId = doc(collection(db, "words")).id;
      let randomQ = query(
        collection(db, "words"),
        where(documentId(), ">=", randomId),
        limit(batchSize + 5)
      );
      let randomSnapshot = await getDocs(randomQ);

      // If not enough, wrap around
      if (randomSnapshot.size < batchSize) {
        const lowQ = query(
          collection(db, "words"),
          where(documentId(), "<", randomId),
          limit(batchSize - randomSnapshot.size + 5)
        );
        const lowSnapshot = await getDocs(lowQ);
        randomSnapshot = { docs: [...randomSnapshot.docs, ...lowSnapshot.docs] };
      }

      let fetchedWords = randomSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Shuffle client-side to mix the batch
      fetchedWords = fetchedWords.sort(() => Math.random() - 0.5);

      if (isAppend) {
        setRecentWords(prev => {
          const existingIds = new Set(prev.map(w => w.word));
          const filteredRandom = fetchedWords.filter(w => !existingIds.has(w.word));
          const newWords = filteredRandom.slice(0, batchSize);
          return [...prev, ...newWords];
        });
        setHasMore(true);
      } else {
        // Filter out pinned
        const pinnedIds = new Set(pinnedWords.map(w => w.word));
        const filteredRandom = fetchedWords.filter(w => !pinnedIds.has(w.word));
        const newWords = filteredRandom.slice(0, batchSize);

        const combined = [...pinnedWords, ...newWords];
        setHasMore(true);
        setRecentWords(combined);
      }

    } catch (err) {
      console.error("Error fetching words:", err);
    } finally {
      if (isAppend) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (refreshTrigger === 0) {
      const params = new URLSearchParams(window.location.search);
      const wordParam = params.get('word');
      if (wordParam) {
        fetchDefinition(wordParam);
        return;
      }
    }
    fetchRandomWords(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, refreshTrigger]);

  const handleRandom = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleLoadMore = () => {
    fetchRandomWords(true);
  };

  // Load dom-to-image dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/dom-to-image/2.6.0/dom-to-image.min.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchDefinition = async (searchWord) => {
    if (!searchWord || searchWord.length < 3) return;
    setLoading(true);
    setError(null);
    setData(null);
    setShowSearch(false);

    try {
      const q = collection(db, "words");
      const querySnapshot = await getDocs(q);
      const allWords = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const searchTerm = normalizeSearch(searchWord);
      const filteredWords = allWords.filter(w => w.word && normalizeSearch(w.word).includes(searchTerm));

      if (filteredWords.length === 0) {
        throw new Error(t.errorTitle || 'Word not found');
      }

      setRecentWords(filteredWords);
      setHasMore(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async (prefix) => {
    if (!prefix || prefix.length < 3) return [];

    try {
      const q = collection(db, "words");
      const snapshot = await getDocs(q);
      const p = normalizeSearch(prefix);

      const matches = snapshot.docs
        .map(doc => doc.data().word)
        .filter(word => word && normalizeSearch(word).includes(p))
        .slice(0, 5);

      return matches;
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      return [];
    }
  };

  const handleSearch = (wordToSearch) => {
    fetchDefinition(wordToSearch);
  };

  const downloadCard = () => {
    if (cardRef.current && cardRef.current.triggerDownload) {
      cardRef.current.triggerDownload();
    } else {
      alert(t.imageGenNotReady || "Download not ready");
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f0f0] font-sans flex flex-col items-center" >

      {/* Top Navigation Bar */}
      <Navbar
        user={user}
        isAdmin={isAdmin}
        onShowAddWord={() => setShowAddWord(true)}
        onShowProfile={() => setShowProfile(true)}
        onLogin={() => setShowLogin(true)}
        onLogout={() => signOut(auth)}
        onToggleSearch={() => setShowSearch(true)}
        onRandom={handleRandom}
        lang={lang}
        t={t}
      />

      <div className="w-full flex flex-col items-center p-4 md:p-8 pt-24 md:pt-32">

        {/* Header Area (Logo Only) */}
        <header
          onClick={() => window.location.href = '/'}
          className="mb-12 text-center w-full max-w-4xl cursor-pointer hover:scale-105 transition-transform duration-300"
        >
          <h1
            ref={headerRef}
            className={`font-black uppercase tracking-tighter mb-4 ${headerFontSize ? '' : 'text-5xl md:text-7xl'}`}
            style={{
              textShadow: '4px 4px 0px #a50000',
              fontSize: headerFontSize ? `${headerFontSize}px` : undefined,
              lineHeight: 1.1
            }}>
            DEVRİMCİ<span className="text-[#7ED957]">SÖZLÜ</span><span className="text-[#FF66C4]">k</span>.
          </h1>
          <p className="text-xl font-bold border-2 border-black inline-block px-4 py-1 bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            {t.headerSubtitle}
          </p>
        </header>

        {/* Mobile Theme Palette */}
        <div className="w-full max-w-sm mb-8 lg:hidden flex justify-center">
          <ThemePalette theme={theme} setTheme={setTheme} compact={true} className="w-full" />
        </div>

        {/* Search Modal */}
        {showSearch && (
          <SearchModal
            onClose={() => setShowSearch(false)}
            onSearch={handleSearch}
            fetchSuggestions={fetchSuggestions}
            t={t}
          />
        )}

        {/* Main Content Area */}
        <div className="w-full max-w-6xl flex flex-col lg:flex-row gap-8 items-start justify-center" >

          {/* Left Side: Controls & Info */}
          < div className="w-full lg:w-1/3 flex flex-col gap-6 order-2 lg:order-1" >

            {/* Theme Selector */}
            <div className="hidden lg:block">
              <ThemePalette theme={theme} setTheme={setTheme} />
            </div>

            {/* Language Selector */}
            <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <h3 className="text-xl font-black uppercase mb-4 flex items-center gap-2">
                Language
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setLang('en')}
                  className={`flex-1 py-2 font-black border-2 border-black ${lang === 'en' ? 'bg-[#5CE1E6] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-white hover:bg-gray-100'}`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang('tr')}
                  className={`flex-1 py-2 font-black border-2 border-black ${lang === 'tr' ? 'bg-[#FF66C4] shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-white hover:bg-gray-100'}`}
                >
                  TR
                </button>
              </div>
            </div>

            {/* Download Action (Only if data is shown) */}
            {
              data && (
                <button
                  onClick={downloadCard}
                  className="w-full py-4 bg-[#7ED957] border-4 border-black font-black text-xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[8px] active:translate-y-[8px] transition-all flex items-center justify-center gap-2"
                >
                  {t.downloadCard} <Download size={24} strokeWidth={3} />
                </button>
              )
            }

            {/* Instructions / State */}
            {
              !data && !loading && !error && (
                <div className="bg-[#5CE1E6] border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                  <h3 className="text-xl font-black uppercase mb-2">{t.welcomeTitle}</h3>
                  <p className="font-bold">{t.welcomeText}</p>
                </div>
              )
            }

            {/* Loading State */}
            {
              loading && (
                <div className="bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] animate-pulse">
                  <div className="h-8 bg-gray-200 w-1/2 mb-4 border-2 border-black"></div>
                  <div className="h-4 bg-gray-200 w-3/4 mb-2 border-2 border-black"></div>
                  <div className="h-4 bg-gray-200 w-full border-2 border-black"></div>
                </div>
              )
            }

            {/* Error State */}
            {
              error && (
                <div className="bg-[#FF66C4] border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex items-start gap-3">
                  <AlertCircle size={32} strokeWidth={3} className="shrink-0" />
                  <div>
                    <h3 className="text-xl font-black uppercase">{t.errorTitle}</h3>
                    <p className="font-bold">{error}{t.errorSuffix}</p>
                  </div>
                </div>
              )
            }
          </div >

          {/* Right Side: The Card or Feed */}
          <div className="w-full lg:w-2/3 flex flex-col items-center gap-8 order-1 lg:order-2" >
            {data ? (
              <WordCard data={data} theme={theme} ref={cardRef} lang={lang} />
            ) : (
              // Feed
              <div className="w-full flex flex-col items-center gap-8">
                {recentWords.length > 0 ? (
                  recentWords.map((wordData, idx) => (
                    <WordCard key={idx} data={wordData} theme={theme} lang={lang} />
                  ))
                ) : (
                  !loading && (
                    <div className="hidden lg:flex w-full aspect-[4/5] border-4 border-dashed border-gray-400 items-center justify-center text-gray-400 font-black text-3xl uppercase tracking-widest">
                      {t.noWords}
                    </div>
                  )
                )}
                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-8 py-3 bg-white border-4 border-black font-black text-xl uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loadingMore ? '...' : t.loadMore}
                  </button>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        < footer className="mt-20 mb-8 text-center font-bold text-gray-500" >
          <p>{t.footerText}</p>
        </footer >

        {/* Login Modal */}
        {showLogin && <LoginModal onClose={() => setShowLogin(false)} lang={lang} t={t} />}

        {/* Add Word Modal (Used for Add and Edit) */}
        {
          showAddWord && (
            <AddWordModal
              onClose={() => {
                setShowAddWord(false);
                setEditingWord(null); // Clear editing state
              }}
              initialData={editingWord}
              isAdmin={isAdmin}
              lang={lang}
              t={t}
            />
          )
        }

        {/* Profile Modal */}
        {
          showProfile && (
            <ProfileModal
              onClose={() => setShowProfile(false)}
              onEditWord={handleEditWord}
              lang={lang}
              t={t}
            />
          )
        }
      </div>
    </div>
  );
};

export default NeobrutalDictionary;